import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { getAppearancePreferences, updateAppearancePreferences } from '../api/preferences';
import UserAuthStore from '../store/userStore';
import { COLOR_SCHEMES } from './colorSchemes';

export { COLOR_SCHEMES } from './colorSchemes';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const getPreferredTheme = () => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) return savedTheme;
    return 'light';
  };

  const [theme, setTheme] = useState(getPreferredTheme);
  const [colorScheme, setScheme] = useState(() => localStorage.getItem('colorScheme') || 'docs');
  const [appearanceError, setAppearanceError] = useState(null);
  const localThemeVersion = useRef(0);
  const localSchemeVersion = useRef(0);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    const favicon = document.getElementById('photon-favicon');
    if (favicon) favicon.href = `${process.env.PUBLIC_URL || ''}/photon-favicon-${theme}.png?v=penrose`;
  }, [theme]);

  useEffect(() => {
    document.documentElement.dataset.photonColorScheme = colorScheme;
  }, [colorScheme]);

  const setColorScheme = useCallback((newScheme) => {
    if (!COLOR_SCHEMES.some(scheme => scheme.value === newScheme)) throw new Error('Invalid color scheme');
    localSchemeVersion.current += 1;
    const requestVersion = localSchemeVersion.current;
    setAppearanceError(null);
    localStorage.setItem('colorScheme', newScheme);
    setScheme(newScheme);
    const { orgId, user } = UserAuthStore.getState();
    if (orgId && user?.uid) {
      updateAppearancePreferences(orgId, user.uid, undefined, newScheme).catch(() => {
        if (requestVersion === localSchemeVersion.current) setAppearanceError('Could not sync appearance preferences. Your selection is saved on this device.');
      });
    }
  }, []);

  const toggleTheme = useCallback(() => {
    localThemeVersion.current += 1;
    const requestVersion = localThemeVersion.current;
    setAppearanceError(null);
    setTheme((prevTheme) => {
      const newTheme = prevTheme === 'dark' ? 'light' : 'dark';
      localStorage.setItem('theme', newTheme);

      // Persist to DB in background — fire and forget
      const { orgId, user } = UserAuthStore.getState();
      if (orgId && user?.uid) {
        updateAppearancePreferences(orgId, user.uid, newTheme).catch(() => {
          if (requestVersion === localThemeVersion.current) setAppearanceError('Could not sync appearance preferences. Your selection is saved on this device.');
        });
      }

      return newTheme;
    });
  }, []);

  // Called once post-auth to sync DB theme into local state.
  // localStorage acts as an instant cache; DB is the source of truth.
  const loadRemoteTheme = useCallback(async (orgId, userId) => {
    if (!orgId || !userId) return;
    const requestThemeVersion = localThemeVersion.current;
    const requestSchemeVersion = localSchemeVersion.current;
    try {
      const response = await getAppearancePreferences(orgId, userId);
      const remoteTheme = response?.data?.theme;
      const remoteScheme = response?.data?.color_scheme;
      if (
        requestThemeVersion === localThemeVersion.current &&
        remoteTheme &&
        remoteTheme !== localStorage.getItem('theme')
      ) {
        localStorage.setItem('theme', remoteTheme);
        setTheme(remoteTheme);
      }
      if (
        requestSchemeVersion === localSchemeVersion.current &&
        COLOR_SCHEMES.some(scheme => scheme.value === remoteScheme)
      ) {
        localStorage.setItem('colorScheme', remoteScheme);
        setScheme(remoteScheme);
      }
    } catch {
      // Remote fetch failed — keep local preference
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, colorScheme, setColorScheme, appearanceError, loadRemoteTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
